// PostHog helpers for Mosqify PWA (init is done by PostHogProvider in index.js)
// Docs: https://posthog.com/docs/libraries/js

import posthog from 'posthog-js';

/** Capture a custom event. Safe to call even if PostHog not inited. */
export function captureEvent(eventName, properties = {}) {
  try {
    posthog.capture(eventName, properties);
  } catch {
    // Swallow analytics errors
  }
}

/** Current browser route helper for diagnostics. */
export function getCurrentRoute() {
  try {
    return window.location?.pathname || 'unknown';
  } catch {
    return 'unknown';
  }
}

/** Identify user (e.g. after login). */
export function identifyUser(distinctId, properties = {}) {
  if (!distinctId) return;
  try {
    posthog.identify(distinctId, properties);
  } catch {
    // Swallow analytics errors
  }
}

/** Reset PostHog state (e.g. on logout). */
export function resetPosthog() {
  try {
    posthog.reset();
  } catch {
    // Swallow analytics errors
  }
}

/** Capture basic web vitals (LCP/CLS/INP) via PerformanceObserver. */
export function startWebVitalsCapture() {
  if (typeof window === 'undefined' || typeof PerformanceObserver === 'undefined') {
    return () => {};
  }

  const cleanups = [];

  const register = (type, eventName, toValue) => {
    try {
      const observer = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const latest = entries[entries.length - 1];
        if (!latest) return;
        captureEvent(eventName, {
          value: toValue(latest),
          route: getCurrentRoute(),
        });
      });
      observer.observe({ type, buffered: true });
      cleanups.push(() => observer.disconnect());
    } catch {
      // Ignore unsupported metric type.
    }
  };

  register('largest-contentful-paint', 'web_vital_lcp', (entry) => Math.round(entry.startTime));
  register('layout-shift', 'web_vital_cls', (entry) => Number((entry.value || 0).toFixed(4)));
  register('event', 'web_vital_inp', (entry) => Math.round(entry.duration || 0));

  return () => {
    cleanups.forEach((fn) => fn());
  };
}
